import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import { CallType, RelayCallState } from 'features/call/call-base/enums';
import { VrsCallType } from 'features/call/vrs-call/enums';
import { shouldShowLeaveQueueButton } from 'features/call/vrs-call/helpers';
import { shouldShowLeaveQueueButtonVri } from 'features/call/vri-call/helpers';
import { Mode } from 'features/multi-mode/enums';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { userIdSelector, userSelector } from 'features/user/store';

export const callIdSelector = ({ call }: RootState) => call.id;

export const callSelector = ({ call }: RootState) => call;

export const callLanguageCodeSelector = ({ call }: RootState) =>
  call.languageCode;

export const callTakenTimeSelector = ({ call }: RootState) =>
  call.callTakenTime;

export const callTypeSelector = ({ call }: RootState) => call.callType;

export const isDeafToHearingSelector = ({ call }: RootState) =>
  call.isDeafToHearing;

export const isHearingToDeafSelector = ({ call }: RootState) =>
  !call.isDeafToHearing;

export const isRelayCallStateInProgressSelector = ({ call }: RootState) =>
  call.relayCallState === RelayCallState.InProg;

export const isRelayCallStateInvalidSelector = ({ call }: RootState) =>
  call.relayCallState === RelayCallState.Invalid;

export const isCallTakenTimeExistSelector = ({ call }: RootState) =>
  Boolean(call.callTakenTime);

export const selectCanRelayTransferCallToMercury = ({ call }: RootState) =>
  call.isCanBeTransferred;

export const isHearingCallerIdBlockedSelector = ({ call }: RootState) =>
  call.isHearingCallerIdBlocked;

export const selectIsAutoCompleteEnabled = ({ call }: RootState) =>
  call.isAutoCompleteEnabled;

// Mercury does not allow the VI to toggle autocomplete for emergency calls.
export const selectIsAutoCompleteToggleEnabled = ({
  call,
  vrsCall,
}: RootState) =>
  call.callType !== CallType.EMERGENCY_CALL &&
  vrsCall.callType !== VrsCallType.EMERGENCY_CALL;

export const isConferenceCall = ({ call }: RootState) =>
  call.callType === CallType.CONFERENCE_CALL;

export const callerConnectionTypeSelector = ({ call }: RootState) =>
  call.callerConnectionType;

export const terminationTypeSelector = ({ call }: RootState) =>
  call.terminationType;

export const sipCallIdSelector = ({ call }: RootState) => call.sipCallId;

export const sipHeaderFromAddressSelector = ({ call }: RootState) =>
  call.sipHeaderFrom;

export const lastCallIdSelector = ({ call }: RootState) => call.lastCallId;

export const endpointMacAddressSelector = ({ call }: RootState) =>
  call.endpointMacAddress;

export const peerUserAgentSelector = ({ call }: RootState) =>
  call.peerUserAgent;

export const selectAreRequiredApiCallsPending = ({ call }: RootState) =>
  call.connectionStateHistoryCount > 0;

export const isColdHandoffCallSelector = ({ call }: RootState) =>
  call.callType === CallType.COLD_HANDOFF_CALL;

export const selectDeafIpTraceback = ({ call }: RootState) =>
  call.DeafIpTraceback;

export const calleeConnectionStringSelector = ({ call }: RootState) =>
  call.calleeConnectionString;

export const selectIsConferenceCallType = createSelector(
  callTypeSelector,
  (callType) =>
    callType === CallType.CONFERENCE_CALL ||
    callType === CallType.TEAMING_CONFERENCE_CALL
);

export const selectActualCallOwnerUserId = createSelector(
  ({ call }: RootState) => call.replacedCallOwnerUserId,
  userIdSelector,
  (replacedCallOwnerUserId, userId) => replacedCallOwnerUserId ?? userId
);

export const selectActualCallOwnerName = createSelector(
  ({ call }: RootState) => call.replacedCallOwnerName,
  userSelector,
  (replacedCallOwnerName, user) =>
    replacedCallOwnerName ?? `${user.firstName} ${user.lastName}`.trim()
);

export const selectHasReplacedCallOwner = createSelector(
  ({ call }: RootState) => call.replacedCallOwnerUserId,
  isVrsModeSelector,
  (replacedCallOwnerUserId, isVrsMode) =>
    isVrsMode && replacedCallOwnerUserId != null
);

// Mocking, selectors, imports, and breakage.
// That's why these selectors in the following array
// are hand-written and not imported.

const createAppSelector = createSelector.withTypes<RootState>();

export const selectCanLeaveQueue = createAppSelector(
  [
    (state) => state.callUiManager.isTakingCall,
    (state) => state.multiMode.mode,
    (state) => state.call.callType,
    (state) => state.vrsCall.callType,
  ],
  (isTakingCall, mode, callType, vrsCallType) => {
    // Main factor for all modes and call types:
    if (isTakingCall) {
      return false;
    }

    if (mode === Mode.VRS) {
      return shouldShowLeaveQueueButton(callType, vrsCallType);
    } else {
      return shouldShowLeaveQueueButtonVri(callType);
    }
  }
);
